import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/auth-provider';

interface ProfileData {
  profile: {
    id: string;
    name: string;
    email: string;
    phone_number: string;
    avatar_url: string;
    created_at: string;
    updated_at: string;
  };
  roles: {
    platform_role: string;
    org_role: string;
    is_admin: boolean;
    is_platform_admin: boolean;
    is_org_admin: boolean;
  };
  stats: {
    bookings_count: number;
    addresses_count: number;
    contacts_count: number;
  };
  recent_activity: Array<{
    event_type: string;
    description: string;
    created_at: string;
    booking_id: string;
  }>;
}

interface AddressData {
  addresses: Array<{
    id: string;
    label: string;
    street: string;
    street2: string;
    city: string;
    province: string;
    postal_code: string;
    country: string;
    created_at: string;
    usage_count: number;
  }>;
  total_count: number;
  has_more: boolean;
}

interface ContactData {
  contacts: Array<{
    id: string;
    name: string;
    phone: string;
    email: string;
    label: string;
    created_at: string;
    is_frequently_used: boolean;
  }>;
  total_count: number;
  has_more: boolean;
}

interface NotificationPreferences {
  email_notifications: boolean;
  sms_notifications: boolean;
  booking_updates: boolean;
  marketing_emails: boolean;
  created_at: string;
  updated_at: string;
}

export const useProfileData = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['profile-complete', user?.id],
    queryFn: async (): Promise<ProfileData> => {
      if (!user?.id) throw new Error('No user found');

      const { data, error } = await supabase.rpc('get_user_profile_complete', {
        p_auth_user_id: user.id
      });

      if (error) throw error;
      return data as unknown as ProfileData;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useAddressesData = (limit = 10, offset = 0) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['addresses-summary', user?.id, limit, offset],
    queryFn: async (): Promise<AddressData> => {
      if (!user?.id) throw new Error('No user found');

      const { data, error } = await supabase.rpc('get_user_addresses_summary', {
        p_auth_user_id: user.id,
        p_limit: limit,
        p_offset: offset
      });

      if (error) throw error;
      return data as unknown as AddressData;
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useContactsData = (limit = 10, offset = 0) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['contacts-summary', user?.id, limit, offset],
    queryFn: async (): Promise<ContactData> => {
      if (!user?.id) throw new Error('No user found');

      const { data, error } = await supabase.rpc('get_user_contacts_summary', {
        p_auth_user_id: user.id,
        p_limit: limit,
        p_offset: offset
      });

      if (error) throw error;
      return data as unknown as ContactData;
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useNotificationPreferences = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['notification-preferences', user?.id],
    queryFn: async (): Promise<NotificationPreferences> => {
      if (!user?.id) throw new Error('No user found');

      const { data, error } = await supabase.rpc('get_user_notification_preferences', {
        p_auth_user_id: user.id
      });

      if (error) throw error;
      return data as unknown as NotificationPreferences;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};